import { useEffect, useRef, useState } from "react";
import { ApiError } from "~/api/errors";
import { useApi } from "~/api/provider";
import type { ReasoningApi, ReasoningOperation } from "~/api/reasoningTypes";
import {
  clearReasoningRecovery,
  readReasoningRecovery,
  reasoningRunActionKey,
  saveReasoningRecovery,
} from "~/features/reasoning/recoveryStorage";

/**
 * Every command the run tab can send, with the exact input it will replay. The journal
 * (`docit:reasoning-run-action:1:<runId>`) is written BEFORE dispatch, so a lost response is
 * retried with the same command id and the server's receipt replay keeps it to one effect.
 */
export type ActionSpec =
  | { kind: "search-start"; input: Parameters<ReasoningApi["startSearch"]>[0] }
  | { kind: "search-advance"; input: Parameters<ReasoningApi["advanceSearch"]>[0] }
  | { kind: "execute"; input: Parameters<ReasoningApi["execute"]>[0] }
  | { kind: "start"; input: Parameters<ReasoningApi["start"]>[0] }
  | { kind: "accept"; input: Parameters<ReasoningApi["accept"]>[0] }
  | { kind: "step"; input: Parameters<ReasoningApi["step"]>[0] }
  | { kind: "followup"; input: Parameters<ReasoningApi["followupObjective"]>[0] }
  | { kind: "postmortem"; input: Parameters<ReasoningApi["submitPostmortem"]>[0] }
  | { kind: "dreamer"; input: Parameters<ReasoningApi["processPostmortem"]>[0] }
  | {
      kind: "experiment-plan";
      input: Omit<Parameters<ReasoningApi["planDreamerExperiment"]>[0], "experimentId">;
    }
  | { kind: "experiment-measure"; input: Parameters<ReasoningApi["measureDreamerExperiment"]>[0] }
  | { kind: "experiment-publish"; input: Parameters<ReasoningApi["publishDreamerExperiment"]>[0] }
  | {
      kind: "experiment-rollback";
      input: Parameters<ReasoningApi["rollbackDreamerExperiment"]>[0];
    }
  | { kind: "refresh-context"; input: Parameters<ReasoningApi["refresh"]>[0] }
  | { kind: "contribute"; input: Parameters<ReasoningApi["contribute"]>[0] }
  | { kind: "assess-evidence"; input: Parameters<ReasoningApi["assessEvidence"]>[0] }
  | { kind: "replan-propose"; input: Parameters<ReasoningApi["proposeReplan"]>[0] }
  | { kind: "replan-apply"; input: Parameters<ReasoningApi["applyReplan"]>[0] }
  | { kind: "correct-source"; input: Parameters<ReasoningApi["correctSource"]>[0] }
  | { kind: "branch-activate"; input: Parameters<ReasoningApi["activateBranches"]>[0] }
  | { kind: "branch-retry"; input: Parameters<ReasoningApi["retryBranch"]>[0] }
  | { kind: "branch-join"; input: Parameters<ReasoningApi["joinBranches"]>[0] }
  | { kind: "branch-cancel"; input: Parameters<ReasoningApi["cancelBranches"]>[0] };
export type ActionKind = ActionSpec["kind"];
export type PendingAction = ActionSpec & { id: string; applied: boolean };

export const ACTION_KINDS: readonly ActionKind[] = [
  "search-start",
  "search-advance",
  "execute",
  "start",
  "accept",
  "step",
  "followup",
  "postmortem",
  "dreamer",
  "experiment-plan",
  "experiment-measure",
  "experiment-publish",
  "experiment-rollback",
  "refresh-context",
  "contribute",
  "assess-evidence",
  "replan-propose",
  "replan-apply",
  "correct-source",
  "branch-activate",
  "branch-retry",
  "branch-join",
  "branch-cancel",
];

export function readPendingAction(runId: string): PendingAction | null {
  try {
    const raw = readReasoningRecovery(reasoningRunActionKey(runId));
    if (!raw) return null;
    const value = JSON.parse(raw) as PendingAction;
    if (
      !value.id ||
      typeof value.applied !== "boolean" ||
      !value.input ||
      !ACTION_KINDS.includes(value.kind)
    )
      return null;
    return value;
  } catch {
    return null;
  }
}

/** A refusal the server will repeat: nothing to retry, the journal entry is dropped. */
const isFinalRefusal = (error: unknown) =>
  error instanceof ApiError &&
  error.status >= 400 &&
  error.status < 500 &&
  error.status !== 408 &&
  error.status !== 429;

export interface ActOutcome {
  kind: ActionKind;
  result: unknown;
}

export function usePendingAction(runId: string, refresh: () => Promise<unknown>) {
  const api = useApi();
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(() =>
    readPendingAction(runId),
  );
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  useEffect(() => {
    setPendingAction(readPendingAction(runId));
    setError("");
    inFlight.current = false;
  }, [runId]);

  const dispatch = (command: PendingAction): Promise<unknown> => {
    const id = command.id;
    const r = api.reasoning;
    switch (command.kind) {
      case "search-start":
        return r.startSearch({ ...command.input, commandId: id });
      case "search-advance":
        return r.advanceSearch({ ...command.input, commandId: id });
      case "execute":
        return r.execute({ ...command.input, commandId: id });
      case "start":
        return r.start({ ...command.input, commandId: id, sessionId: id });
      case "accept":
        return r.accept({ ...command.input, commandId: id });
      case "step":
        return r.step({ ...command.input, commandId: id });
      case "followup":
        return r.followupObjective({ ...command.input, commandId: id });
      case "postmortem":
        return r.submitPostmortem({ ...command.input, commandId: id });
      case "dreamer":
        return r.processPostmortem(command.input);
      case "experiment-plan":
        return r.planDreamerExperiment({ ...command.input, experimentId: id });
      case "experiment-measure":
        return r.measureDreamerExperiment(command.input);
      case "experiment-publish":
        return r.publishDreamerExperiment(command.input);
      case "experiment-rollback":
        return r.rollbackDreamerExperiment(command.input);
      case "refresh-context":
        return r.refresh({ ...command.input, commandId: id });
      case "contribute":
        return r.contribute({ ...command.input, commandId: id });
      case "assess-evidence":
        return r.assessEvidence({ ...command.input, commandId: id });
      case "replan-propose":
        return r.proposeReplan({ ...command.input, commandId: id });
      case "replan-apply":
        return r.applyReplan({ ...command.input, commandId: id });
      case "correct-source":
        return r.correctSource({ ...command.input, commandId: id });
      case "branch-activate":
        return r.activateBranches({ ...command.input, commandId: id });
      case "branch-retry":
        return r.retryBranch({ ...command.input, commandId: id });
      case "branch-join":
        return r.joinBranches({ ...command.input, commandId: id });
      case "branch-cancel":
        return r.cancelBranches({ ...command.input, commandId: id });
    }
  };

  /**
   * Runs `action`, or replays the journaled one when none is given. Resolves with the applied
   * command's kind and result so the caller can clear a form or keep an execute receipt.
   */
  const act = async (action?: ActionSpec): Promise<ActOutcome | undefined> => {
    if (inFlight.current || (!pendingAction && !action)) return undefined;
    const command: PendingAction | null =
      pendingAction ?? (action ? { ...action, id: crypto.randomUUID(), applied: false } : null);
    if (!command) return undefined;
    inFlight.current = true;
    const key = reasoningRunActionKey(runId);
    saveReasoningRecovery(key, JSON.stringify(command));
    setPendingAction(command);
    setRunning(true);
    setError("");
    let result: unknown;
    try {
      if (!command.applied) {
        result = await dispatch(command);
        command.applied = true;
        saveReasoningRecovery(key, JSON.stringify(command));
      }
      await refresh();
      clearReasoningRecovery(key);
      setPendingAction(null);
      return { kind: command.kind, result };
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      if (!command.applied && isFinalRefusal(cause)) {
        clearReasoningRecovery(key);
        setPendingAction(null);
      }
      await refresh().catch(() => undefined);
      return undefined;
    } finally {
      inFlight.current = false;
      setRunning(false);
    }
  };

  return {
    pendingAction,
    running,
    /** A command is in flight or journaled: no second command until it settles. */
    busy: running || pendingAction !== null,
    error,
    setError,
    act,
  };
}

export const isOperation = (value: unknown): value is ReasoningOperation =>
  !!value && typeof value === "object" && "status" in value && "requestedModel" in value;
